<template>
  <div v-if="isMember" class="flex flex-col gap-2 relative w-full h-full overflow-y-auto pb-4">
    <!-- Progress toward the next reward. A bar rather than two stat boxes: the number
         only means something against the target, and "3" next to "1" said neither. -->
    <div class="flex items-center justify-between mb-2.5">
      <span class="text-[14.5px] font-semibold">מועדון לקוחות</span>
      <span
        class="text-[11.5px] font-medium text-red-600 cursor-pointer"
        @click="removeFromLoyaltyProgram">
        הסרה מהמועדון
      </span>
    </div>

    <div class="bg-white border border-surface-200 rounded-xl px-[13px] py-3">
      <div class="flex items-baseline justify-between">
        <span class="text-[12.5px] text-surface-600">{{ isPointsProgram ? 'נקודות שנצברו' : 'תורים שנצברו' }}</span>
        <span class="text-base font-semibold" style="direction: ltr">
          {{ progress.current }}<span
            v-if="progress.target"
            class="text-xs text-surface-400 font-normal"> / {{ progress.target }}</span>
        </span>
      </div>

      <div v-if="progress.target" class="h-[5px] rounded-full bg-surface-100 mt-2 overflow-hidden">
        <div
          class="h-full rounded-full bg-primary transition-[width] duration-300"
          :style="{ width: `${progress.percent}%` }" />
      </div>

      <div class="text-[11.5px] text-surface-500 mt-2">
        <span v-if="progress.remaining > 0">{{ progress.remainingLabel }}</span>
        <span v-else class="text-emerald-600 font-medium">ההטבה זמינה למימוש 🎉</span>
        <span v-if="loyaltyProgramSummary.totalUsed > 0"> · {{ usedLabel }}</span>
      </div>
    </div>

    <!-- The customer gives this when they join, and often skips it. Editable here so the
         owner can add or fix it without the customer having to redo the sign-up link. -->
    <div
      v-if="businessData?.loyaltyProgram?.birthday?.shouldCollect"
      class="border-b border-surface-100 py-2.5">
      <div class="flex items-center justify-between">
        <span class="text-xs text-surface-500">תאריך יום הולדת</span>
        <span class="text-[11.5px] font-medium text-primary cursor-pointer" @click="toggleEditBirthday">
          {{ editBirthday ? 'ביטול' : (storedBirthday ? 'עריכה' : 'הוספה') }}
        </span>
      </div>

      <div v-if="!editBirthday" class="text-[13.5px] mt-1">
        <span v-if="storedBirthday">{{ formatBirthday(storedBirthday) }}</span>
        <span v-else class="text-surface-400">—</span>
      </div>

      <div v-else class="flex flex-col gap-2 mt-2">
        <!-- Same control the customer fills in when they join the club, so a birthday is
             entered the same way whoever is typing it. -->
        <BirthdayPicker v-model="birthdayDraft" />
        <div class="flex gap-2">
          <Button
            label="שמירה"
            size="small"
            class="!rounded-full flex-1"
            :loading="savingBirthday"
            :disabled="!birthdayChanged"
            @click="saveBirthday" />
          <Button
            v-if="storedBirthday"
            icon="pi pi-trash"
            size="small"
            severity="danger"
            outlined
            class="!rounded-full"
            :disabled="savingBirthday"
            @click="clearBirthday" />
        </div>
      </div>
    </div>

    <template v-if="loyaltyProgramSummary?.hasHistory">
      <span class="text-[14.5px] font-semibold mt-3.5 mb-0.5">היסטוריה</span>
      <loyalty-program-withdraws-deposits-modal
        :clientData="customer"
        :loyaltyProgramPromoType="businessData.loyaltyProgram.promoType" />
    </template>
    <div v-else class="text-center text-[12.5px] text-surface-500 py-6">
      עדיין אין צבירות או מימושים
    </div>
  </div>

  <!-- Not a member: the design system's "sell" empty state — say what it's worth, then
       offer the action. -->
  <div v-else class="w-full pt-2">
    <div class="bg-white border border-dashed border-surface-300 rounded-2xl px-[18px] py-6 text-center">
      <div class="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2.5">
        <i class="pi pi-gift !text-xl text-primary-700" />
      </div>

      <template v-if="businessData?.loyaltyProgram">
        <div class="text-sm font-semibold mb-1">הפכו לקוח מזדמן ללקוח קבוע</div>
        <div class="text-xs text-surface-500 leading-relaxed max-w-[260px] mx-auto mb-3">
          {{ clubPitch }} שליחת קישור אישי — ההצטרפות מתבצעת על ידי הלקוח/ה.
        </div>
        <div class="flex gap-2 justify-center flex-wrap">
          <Button
            label="שליחת הזמנה"
            icon="pi pi-send"
            size="small"
            class="!rounded-full"
            :loading="sendingInvite"
            @click="sendJoinInvite" />
          <Button
            v-if="lastInviteMessage && canViewContact"
            label="שליחה בוואטסאפ"
            icon="pi pi-whatsapp"
            size="small"
            severity="secondary"
            outlined
            class="!rounded-full"
            @click="sendInviteOnWhatsapp" />
        </div>
      </template>

      <template v-else>
        <div class="text-sm font-semibold mb-1">מועדון הלקוחות אינו פעיל</div>
        <div class="text-xs text-surface-500 leading-relaxed max-w-[260px] mx-auto">
          הפעילו מועדון לקוחות בהגדרות כדי לתגמל לקוחות חוזרים.
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import { ICustomer, ILoyaltyProgramPoints, LoyaltyProgramPromosTypes } from '@torim-app/types';
import { calculateDiscountedLP, sendWhatsApp } from '@torim-app/utils';
import { LoyaltyProgramApi } from '@app/api/admin/loyalty-program-api';
import { useAdminStore } from '@app/stores';
import { BirthdayPicker, useDrawer } from '@torim-app/shared-components';
import LoyaltyProgramWithdrawsDepositsModal
  from '@app/components/admin/modals/LoyaltyProgramWithdrawsDepositsModal.vue';
import { CustomersApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import { trackEvent } from '@app/composables/posthog';
import { DateTime } from 'luxon';

const adminStore = useAdminStore();
const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();
const confirm = useConfirm();

const emit = defineEmits(['updated']);
const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true
  }
});

const businessData = computed(() => adminStore.getBusinessSiteData);
// The WhatsApp fallback builds a wa.me link from the number, so it's hidden when the
// employee isn't allowed to see it — the SMS invite still works for them.
const canViewContact = computed(() => adminStore.getCanViewCustomerContact);

const loyaltyProgramSummary = computed<{
  totalUsed: number;
  totalAvailable: number;
  hasHistory: boolean;
}>(() => {
  if (!props.customer.loyalty || !businessData.value.loyaltyProgram)
    return {} as {
      totalUsed: number;
      totalAvailable: number;
      hasHistory: false
    };

  let totalAvailable = 0, totalUsed = 0, hasHistory = false;
  if (businessData.value.loyaltyProgram.promoType === LoyaltyProgramPromosTypes.Point) {
    totalUsed = ((props.customer.loyalty.points ?? {}) as ILoyaltyProgramPoints).withdraws?.reduce((acc, cur) => acc += cur.amount, 0) ?? 0;
    totalAvailable = (((props.customer.loyalty.points ?? {}) as ILoyaltyProgramPoints).deposits?.reduce((acc, cur) => acc += cur.amount, 0) ?? 0) - totalUsed;
    hasHistory = !!props.customer.loyalty?.points?.withdraws?.length || !!props.customer?.loyalty?.points?.deposits?.length;
  } else if (businessData.value.loyaltyProgram.promoType === LoyaltyProgramPromosTypes.Discount && businessData.value.loyaltyProgram.discountOptions && props.customer.loyalty.discount) {
    const {
      appointmentsFromLastReach,
      alreadyUsedBenefits
    } = calculateDiscountedLP(businessData.value.loyaltyProgram.discountOptions, props.customer.loyalty.discount);
    totalUsed = alreadyUsedBenefits;
    totalAvailable = appointmentsFromLastReach;
    hasHistory = !!props.customer.loyalty?.discount?.withdraws?.length || !!props.customer?.loyalty?.discount?.deposits?.length;
  }

  return {
    totalUsed, totalAvailable, hasHistory
  };
});

/** Points accrue fractionally; a trailing ".00" on a whole number is just noise. */
const round = (value: number) => parseFloat(value.toFixed(2));

const isMember = computed(() => Object.keys(loyaltyProgramSummary.value).length > 0);
const isPointsProgram = computed(() =>
  businessData.value?.loyaltyProgram?.promoType === LoyaltyProgramPromosTypes.Point
);

/**
 * Where the customer stands against the next reward.
 *
 * A points program has no fixed target — points are spent like currency — so it shows the
 * balance alone and skips the bar. A discount program counts visits toward a known
 * number, which is what the bar is for.
 */
const progress = computed(() => {
  const current = round(loyaltyProgramSummary.value?.totalAvailable ?? 0);

  if (isPointsProgram.value) {
    return { current, target: 0, percent: 0, remaining: 0, remainingLabel: '' };
  }

  const target = businessData.value?.loyaltyProgram?.discountOptions?.numberOfAppointments ?? 0;
  const remaining = Math.max(0, target - current);
  return {
    current,
    target,
    percent: target > 0 ? Math.min(100, (current / target) * 100) : 0,
    remaining,
    remainingLabel: remaining === 1 ? 'עוד תור אחד להטבה הבאה' : `עוד ${remaining} תורים להטבה הבאה`,
  };
});

const usedLabel = computed(() => {
  const used = round(loyaltyProgramSummary.value?.totalUsed ?? 0);
  if (isPointsProgram.value) return `${used} נקודות מומשו`;
  return used === 1 ? 'מימוש אחד עד כה' : `${used} מימושים עד כה`;
});

/** One-line description of what the club gives, for the not-a-member pitch. */
const clubPitch = computed(() => {
  const program = businessData.value?.loyaltyProgram;
  if (program?.promoType === LoyaltyProgramPromosTypes.Point)
    return `צבירת ${program.pointsOptions?.percentageToGather}% מכל ביקור בנקודות.`;

  const options = program?.discountOptions;
  if (!options) return '';
  // numberOfAppointments = visits completed BEFORE the benefit — the next visit gets it
  return options.discountAmount === 100
    ? `אחרי ${options.numberOfAppointments} תורים — התור הבא בחינם.`
    : `אחרי ${options.numberOfAppointments} תורים — התור הבא ב-${options.discountAmount}% הנחה.`;
});

// ── Birthday ────────────────────────────────────────────────────────────────────────
const editBirthday = ref(false);
const birthdayDraft = ref<Date>();
const savingBirthday = ref(false);

const storedBirthday = computed(() => props.customer?.loyalty?.birthday);

const formatBirthday = (value: Date | string) =>
  DateTime.fromISO(new Date(value).toISOString()).setLocale('he').toFormat('dd/MM/yyyy');

const toIso = (value?: Date) => (value ? DateTime.fromJSDate(value).toISODate() : null);

const birthdayChanged = computed(() => {
  const stored = storedBirthday.value
    ? DateTime.fromISO(new Date(storedBirthday.value).toISOString()).toUTC().toISODate()
    : null;
  return toIso(birthdayDraft.value) !== stored;
});

const toggleEditBirthday = () => {
  editBirthday.value = !editBirthday.value;
  // Re-seed on open so cancelling always drops back to what is stored.
  birthdayDraft.value = editBirthday.value && storedBirthday.value
    ? new Date(storedBirthday.value as string)
    : undefined;
};

const persistBirthday = async (value: string | null, successMessage: string) => {
  savingBirthday.value = true;
  try {
    await CustomersApi.editCustomer({ customerId: props.customer.id!, loyaltyBirthday: value });
    trackEvent('customer_loyalty_birthday_updated', { cleared: value === null });
    toast.success(successMessage);
    editBirthday.value = false;
    emit('updated');
  } catch (err) {
    console.log(err);
    toast.error('לא הצלחנו לעדכן את התאריך, אנא נסו שוב');
  } finally {
    savingBirthday.value = false;
  }
};

const saveBirthday = () => {
  if (!birthdayChanged.value || savingBirthday.value) return;
  persistBirthday(toIso(birthdayDraft.value), 'התאריך עודכן בהצלחה');
};

const clearBirthday = () => {
  confirm.require({
    message: 'למחוק את תאריך יום ההולדת?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: () => persistBirthday(null, 'התאריך נמחק')
  });
};

const sendingInvite = ref(false);
// Kept after a successful send so the owner can also forward the same text on WhatsApp.
const lastInviteMessage = ref<string>('');

const sendJoinInvite = async () => {
  if (sendingInvite.value) return;
  trackEvent('customer_loyalty_invite_sent');
  sendingInvite.value = true;
  try {
    const res = await LoyaltyProgramApi.sendJoinInvite(props.customer.id);
    if (res.alreadyMember) {
      toast.info('הלקוח/ה כבר חבר/ה במועדון');
      emit('updated');
      return;
    }
    lastInviteMessage.value = res.message;
    if (res.smsOptedOut)
      toast.info('הלקוח/ה ביקש/ה לא לקבל הודעות SMS — ניתן לשלוח את הקישור בוואטסאפ');
    else
      toast.success('ההזמנה נשלחה ללקוח/ה');
  } catch (err) {
    console.log(err);
    toast.error('לא הצלחנו לשלוח את ההזמנה, אנא נסו שוב');
  } finally {
    sendingInvite.value = false;
  }
};

const sendInviteOnWhatsapp = () => {
  trackEvent('customer_loyalty_invite_whatsapp_clicked');
  sendWhatsApp(props.customer.phoneNumber, encodeURIComponent(lastInviteMessage.value));
};


const removeFromLoyaltyProgram = () => {
  trackEvent('customer_loyalty_program_removed');
  confirm.require({
    message: '⚠ האם להסיר ממועדון הלקוחות? פעולה זו תמחק את כל ההטבות שנצברו ואינה ניתנת לביטול',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      CustomersApi.removeLoyaltyProgram(props.customer.id)
        .then(() => {
          toast.success('הלקוח הוסר בהצלחה ממועדון הלקוחות');
          emit('updated');
        })
        .catch((err) => {
          console.log(err);
          toast.error('לא הצלחנו להסיר ממועדון הלקוחות, אנא נסו שוב');
        });
    }
  });
};

</script>

<style scoped lang="scss">

</style>
